import React from "react";
import { Card } from "antd";
import { Sourcing, SourcingStatus } from "../interface";

interface SourcingFlowProps {
  record: Sourcing;
}

export const SourcingFlow: React.FC<SourcingFlowProps> = ({ record }) => {
  const getStatusColor = (step: string, currentStatus: SourcingStatus) => {
    const statusOrder = ['DRAFT', 'SUBMITTED', 'APPROVED', 'PUBLISHED', 'CLOSED', 'RESULT_SUBMITTED', 'AWARD_APPROVED', 'AWARDED'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const stepIndex = statusOrder.indexOf(step);

    if (stepIndex <= currentIndex) return '#52c41a'; // completed
    if (stepIndex === currentIndex + 1) return '#1890ff'; // current
    return '#d9d9d9'; // pending
  };

  const getStepDate = (step: string) => {
    const statusOrder = ['DRAFT', 'SUBMITTED', 'APPROVED', 'PUBLISHED', 'CLOSED', 'RESULT_SUBMITTED', 'AWARD_APPROVED', 'AWARDED'];
    const currentIndex = statusOrder.indexOf(record.status);
    const stepIndex = statusOrder.indexOf(step);

    // Don't show date for non-completed steps
    if (stepIndex > currentIndex) return '';

    switch (step) {
      case 'CREATED':
        return record.createdAt ? new Date(record.createdAt).toLocaleString() : 'N/A';
      case 'SUBMITTED':
        return record.submittedAt
          ? new Date(record.submittedAt).toLocaleString()
          : record.submittedBy ? 'Submitted' : 'Not submitted';
      case 'APPROVED': {
        // Get the last approval from the approvals array
        const lastApproval = record.approvals && record.approvals.length > 0
          ? record.approvals.reduce((latest, current) =>
              current.order > latest.order ? current : latest)
          : null;
        return lastApproval?.approvedOn
          ? new Date(lastApproval.approvedOn).toLocaleString()
          : record.approvedBy ? 'Approved' : 'Not approved';
      }
      case 'PUBLISHED':
        return record.publishData?.createdAt
          ? new Date(record.publishData.createdAt).toLocaleString()
          : 'Not published';
      case 'CLOSED':
        return record.openedAt
          ? new Date(record.openedAt).toLocaleString()
          : 'Not closed';
      case 'RESULT_SUBMITTED':
        return record.resultSubmittedAt
          ? new Date(record.resultSubmittedAt).toLocaleString()
          : 'Results not submitted';
      case 'AWARD_APPROVED': {
        // Get the last award approval from the awardApprovals array
        const lastAwardApproval = record.awardApprovals && record.awardApprovals.length > 0
          ? record.awardApprovals.reduce((latest, current) =>
              current.order > latest.order ? current : latest)
          : null;
        return lastAwardApproval?.approvedOn
          ? new Date(lastAwardApproval.approvedOn).toLocaleString()
          : 'Not approved';
      }
      case 'AWARDED':
        return record.awardedAt
          ? new Date(record.awardedAt).toLocaleString()
          : 'Not awarded';
      default:
        return 'N/A';
    }
  };

  const getStepUser = (step: string) => {
    const statusOrder = ['DRAFT', 'SUBMITTED', 'APPROVED', 'PUBLISHED', 'CLOSED', 'RESULT_SUBMITTED', 'AWARD_APPROVED', 'AWARDED'];
    const currentIndex = statusOrder.indexOf(record.status);
    const stepIndex = statusOrder.indexOf(step);

    // Don't show user for non-completed steps
    if (stepIndex > currentIndex) return '';

    switch (step) {
      case 'CREATED':
        return record.createdBy || 'N/A';
      case 'SUBMITTED':
        return record.submittedBy || 'N/A';
      case 'APPROVED': {
        // Get the last approval from the approvals array
        const lastApproval = record.approvals && record.approvals.length > 0
          ? record.approvals.reduce((latest, current) =>
              current.order > latest.order ? current : latest)
          : null;
        return lastApproval?.approvedBy || record.approvedBy || 'N/A';
      }
      case 'PUBLISHED':
        return record.publishData?.createdBy?.id || 'N/A';
      case 'CLOSED':
        return record.openedBy || 'N/A';
      case 'RESULT_SUBMITTED':
        return record.resultSubmittedBy || 'N/A';
      case 'AWARD_APPROVED': {
        // Get the last award approval from the awardApprovals array
        const lastAwardApproval = record.awardApprovals && record.awardApprovals.length > 0
          ? record.awardApprovals.reduce((latest, current) =>
              current.order > latest.order ? current : latest)
          : null;
        return lastAwardApproval?.approvedBy || 'N/A';
      }
      case 'AWARDED':
        return record.awardedBy || 'System';
      default:
        return 'N/A';
    }
  };

  const steps = ['CREATED', 'SUBMITTED', 'APPROVED', 'PUBLISHED', 'CLOSED', 'RESULT_SUBMITTED', 'AWARD_APPROVED', 'AWARDED'];
  const stepLabels = {
    CREATED: 'Created',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    PUBLISHED: 'Published',
    CLOSED: 'Bids Opened and Sourcing Closed for Submission',
    RESULT_SUBMITTED: 'Results Submitted for Award Approval',
    AWARD_APPROVED: 'Award Approved',
    AWARDED: 'Awarded'
  };

  return (
    <Card title="Sourcing Flow" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(step, record.status),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '12px'
                }}
              >
                {index + 1}
              </div>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                {stepLabels[step as keyof typeof stepLabels]}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                {getStepDate(step)}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                {getStepUser(step)}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                style={{
                  width: 40,
                  height: 2,
                  backgroundColor: getStatusColor(steps[index + 1], record.status),
                  margin: '0 8px'
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};